<template>
  <OverlayRowsContainer
    :in-expansion-panel="overlays.length > 0"
  >
    <OverlayRow
      v-for="(overlay, index) in localOverlays"
      :key="index"
      :overlay="overlay"
      :is-draggable="overlays.length !== 1"
      @drag-handler="(e) => dragHandler(e, index)"
      @drop-handler="(e, dropPosition) => dropHandler(e, index, dropPosition)"
    />
  </OverlayRowsContainer>
</template>

<script>
// Components
import OverlayRowsContainer from './OverlayRowsContainer.vue';
import OverlayRow from './OverlayRow.vue';

// Tools & Services
import {throttle} from '@northpoint-development/labs-common-core';
import {THROTTLED_UPDATE_DELAY} from '@/constants/InfoWindowContent';

export default {
  name: 'OverlayRows',
  components: {
    OverlayRowsContainer,
    OverlayRow,
  },
  props: {
    overlays: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localOverlays: [],
      updatingOverlays: false,
    };
  },
  watch: {
    overlays() {
      this.localOverlays = this.sortOverlays(this.overlays);
    },
  },
  mounted() {
    // Order the overlays from firestore on mount
    this.localOverlays = this.sortOverlays(this.overlays);
    // If there is some overlay that has a parent but does not have an index, update the overlay indices
    const isNotIndexed = this.localOverlays.some((overlay) => overlay.parent && !overlay.parent?.index);
    if (isNotIndexed) {
      // Apply indices based on default sort to firestore
      this.indexOverlays();
    }
  },
  methods: {
    /**
     * sortOverlays takes a list of overlays and sorts them from highest index to lowest index
     * @param {Array<Overlay>} overlays - the list of overlays to sort
     * @return {Array<Overlay>} a sorted list of overlays
     */
    sortOverlays(overlays) {
      return [...overlays].sort((a, b) => {
        const firstIndex = a.parent?.index;
        const secondIndex = b.parent?.index;
        // preserve order (stable sort) when both indexes are null/undefined
        if (firstIndex == null && secondIndex == null) return 0;
        if (firstIndex == null) return -1; // If a does not have an index, push to the end
        if (secondIndex == null) return 1; // If a has an index and b does not, push b to the end
        return secondIndex - firstIndex;
      });
    },
    dragHandler(e, fromIndex) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.dropEffect = 'move';
      e.dataTransfer.setData('from-index', fromIndex);
    },
    /**
     * repositionElement takes an array, moves the from index to the to index, and returns the new array
     * @param {Array<Overlay>} overlays - an array we want to reposition some index in
     * @param {number} fromIndex - The index of the element we are moving in an array
     * @param {number} toIndex - The position we are moving the element to
     * @param {string} dropPosition - string that determines whether we want to move before or after the to index
     * @return {Array<Overlay>} - An array where the index has potentially been repositioned
     */
    repositionElements(overlays, fromIndex, toIndex, dropPosition) {
      // If the index isn't moving, don't change the position
      if (
        (fromIndex === toIndex) ||
        ((toIndex === fromIndex + 1) && dropPosition === 'before') ||
        ((toIndex === fromIndex - 1) && dropPosition === 'after')
      ) {
        return overlays;
      }

      // In some cases, the toIndex needs to be shifted by one
      let newIndex = toIndex;
      if (fromIndex < toIndex && dropPosition === 'before') {
        newIndex--;
      }
      if (fromIndex > toIndex && dropPosition === 'after') {
        newIndex++;
      }

      // Get the overlay at the from index
      const fromOverlay = overlays[fromIndex];
      // Splice it to the new position
      overlays.splice(fromIndex, 1);
      overlays.splice(newIndex, 0, fromOverlay);

      return overlays;
    },
    dropHandler(e, toIndex, dropPosition) {
      const fromIndex = parseInt(e.dataTransfer.getData('from-index'));

      // Given a copy of the local overlays, modify and return a new array
      this.localOverlays = this.repositionElements([...this.localOverlays], fromIndex, toIndex, dropPosition);

      // Index the overlays
      this.indexOverlays();
    },
    async indexOverlays() {
      // throttle a call to Promise.all
      // Promise.all enables the ability to run the inner async functions in parallel
      // The inner async functions are invoked immediately upon creation
      // The inner async functions are responsible for updating overlays with their index in the projefct
      throttle(await Promise.all(this.localOverlays.map((overlay, index) => {
        return (async () => {
          await overlay.update({
            parent: {
              ...overlay.parent,
              index: this.overlays.length - index,
            },
          }, false);
        })();
      })), THROTTLED_UPDATE_DELAY);
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
